import { useEffect, useState } from "react";
import { api, STATUS_COLORS } from "../api";
import { useAuth } from "../context/AuthContext";
import { useLang, fmtRM } from "../i18n";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import PayslipModal from "../components/PayslipModal";
import { CheckCircle2, Circle, Loader2, AlertTriangle, Play, Calculator, ShieldCheck, Lock, Eye } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["OPEN", "CALCULATED", "VALIDATED", "APPROVED", "LOCKED"];

export default function Payroll() {
  const { tenant } = useAuth();
  const { lang } = useLang();
  const bm = lang === "bm";
  const [runs, setRuns] = useState([]);
  const [run, setRun] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [busy, setBusy] = useState("");
  const [slip, setSlip] = useState(null);

  const loadRuns = () => api.get("/payroll/runs").then((r) => {
    setRuns(r.data);
    if (r.data.length && !run) selectRun(r.data[0].run_id);
    else if (run) selectRun(run.run_id);
  }).catch(() => {});
  const selectRun = (id) => {
    api.get(`/payroll/runs/${id}`).then((r) => {
      setRun(r.data);
      if (["CALCULATED", "VALIDATED", "APPROVED", "LOCKED"].includes(r.data.status)) {
        api.get("/payroll/comparison", { params: { period: r.data.period } }).then((c) => setComparison(c.data)).catch(() => {});
      } else setComparison(null);
    });
  };
  useEffect(() => { loadRuns(); }, []);

  const act = async (action) => {
    setBusy(action);
    try {
      const r = await api.post(`/payroll/runs/${run.run_id}/${action}`);
      toast.success(`${action} OK`);
      if (action === "validate" && r.data.issues?.length === 0) toast.success(bm ? "Tiada isu pengesahan" : "No validation issues");
      await loadRuns();
      selectRun(run.run_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
    setBusy("");
  };

  const stepIdx = run ? STEPS.indexOf(run.status) : -1;
  const stepMeta = [
    { key: "OPEN", label: bm ? "Buka Payroll" : "Open", icon: Play, testid: "payroll-run-step-1-open" },
    { key: "CALCULATED", label: bm ? "Kira Statutory" : "Calculate", icon: Calculator, testid: "payroll-run-step-2-calculate" },
    { key: "VALIDATED", label: bm ? "Sahkan" : "Validate", icon: ShieldCheck, testid: "payroll-run-step-3-validate" },
    { key: "APPROVED", label: bm ? "Lulus" : "Approve", icon: CheckCircle2, testid: "payroll-run-step-4-approve" },
    { key: "LOCKED", label: bm ? "Kunci & Payslip" : "Lock", icon: Lock, testid: "payroll-run-step-5-lock" },
  ];

  return (
    <div data-testid="payroll-page" className="space-y-6">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{bm ? "Payroll Malaysia" : "Malaysian Payroll"}</h1>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <Card className="lg:col-span-1">
          <CardContent className="pt-4 space-y-2">
            {runs.map((r) => (
              <button key={r.run_id} data-testid={`payroll-run-${r.period}`} onClick={() => selectRun(r.run_id)}
                className={`w-full flex items-center justify-between border rounded-lg px-3 py-2.5 text-left transition-colors ${run?.run_id === r.run_id ? "border-blue-700 bg-blue-50 dark:bg-blue-950/40" : "hover:bg-slate-50 dark:hover:bg-slate-800"}`}>
                <span className="font-mono font-semibold text-sm">{r.period}</span>
                <Badge variant="outline" className={STATUS_COLORS[r.status] || ""}>{r.status}</Badge>
              </button>
            ))}
          </CardContent>
        </Card>

        <div className="lg:col-span-3 space-y-4">
          {run && (
            <>
              <Card>
                <CardContent className="pt-5">
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    {stepMeta.map((s, i) => {
                      const done = i <= stepIdx;
                      const next = i === stepIdx + 1;
                      const actionMap = { CALCULATED: "calculate", VALIDATED: "validate", APPROVED: "approve", LOCKED: "lock" };
                      return (
                        <div key={s.key} className="flex items-center gap-2">
                          {i > 0 && <div className={`w-6 sm:w-10 h-px ${done ? "bg-blue-700" : "bg-slate-200 dark:bg-slate-700"}`} />}
                          <button data-testid={s.testid} disabled={!next || !!busy} onClick={() => next && act(actionMap[s.key])}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold border transition-all
                              ${done ? "bg-blue-800 text-white border-blue-800" : next ? "border-blue-700 text-blue-800 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950 cursor-pointer" : "text-slate-400 border-slate-200 dark:border-slate-700"}`}>
                            {busy === actionMap[s.key] ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : done ? <s.icon className="w-3.5 h-3.5" /> : <Circle className="w-3.5 h-3.5" />}
                            {s.label}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {run.totals?.gross != null && run.status !== "OPEN" && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {[
                    [bm ? "Gaji Kasar" : "Gross Payroll", run.totals.gross, comparison?.deltas?.gross],
                    [bm ? "Gaji Bersih" : "Net Pay", run.totals.net, null],
                    ["OT", run.totals.ot_pay, comparison?.deltas?.ot],
                    [bm ? "Kos Majikan" : "Employer Cost", run.totals.employer_cost, comparison?.deltas?.employer_cost],
                  ].map(([label, val, delta], i) => (
                    <div key={i} className="stat-card">
                      <p className="text-xs font-medium uppercase tracking-wider text-slate-400">{label}</p>
                      <p className="mt-1 text-xl font-extrabold font-mono">{fmtRM(val)}</p>
                      {delta != null && (
                        <p className={`text-xs font-semibold mt-0.5 ${delta >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                          {delta >= 0 ? "+" : ""}{delta}% vs {comparison?.previous?.period}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {comparison?.alerts?.length > 0 && (
                <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/30">
                  <CardContent className="pt-4 space-y-1">
                    {comparison.alerts.map((a, i) => (
                      <p key={i} data-testid={`payroll-alert-${i}`} className="text-sm text-amber-800 dark:text-amber-300 flex gap-2">
                        <AlertTriangle className="w-4 h-4 shrink-0" /> {a}
                      </p>
                    ))}
                  </CardContent>
                </Card>
              )}

              {run.validation_issues?.length > 0 && (
                <Card className="border-rose-200">
                  <CardContent className="pt-4 space-y-1.5">
                    <p className="text-sm font-semibold text-rose-700">{bm ? "Isu Pengesahan" : "Validation Issues"} ({run.validation_issues.length})</p>
                    {run.validation_issues.map((v, i) => (
                      <p key={i} className="text-xs text-slate-600 flex gap-2">
                        <Badge variant="outline" className={v.severity === "HIGH" ? "bg-rose-50 text-rose-700 border-rose-200" : "bg-amber-50 text-amber-700 border-amber-200"}>{v.severity}</Badge>
                        <span><b>{v.employee}</b> — {v.issue}</span>
                      </p>
                    ))}
                  </CardContent>
                </Card>
              )}

              {run.payslips?.length > 0 && (
                <div className="bg-card border rounded-xl overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>{bm ? "Pekerja" : "Employee"}</TableHead>
                        <TableHead className="text-right">{bm ? "Kasar" : "Gross"}</TableHead>
                        <TableHead className="text-right hidden md:table-cell">EPF (E/E)</TableHead>
                        <TableHead className="text-right hidden md:table-cell">PCB</TableHead>
                        <TableHead className="text-right">{bm ? "Bersih" : "Net"}</TableHead>
                        <TableHead />
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {run.payslips.map((s) => (
                        <TableRow key={s.payslip_id} data-testid={`payslip-row-${s.emp_no}`}>
                          <TableCell>
                            <p className="text-sm font-medium">{s.employee_name}</p>
                            <p className="text-xs text-slate-500 font-mono">{s.emp_no}</p>
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm">{fmtRM(s.earnings.gross)}</TableCell>
                          <TableCell className="text-right font-mono text-sm hidden md:table-cell">{fmtRM(s.deductions.epf_employee)} / {fmtRM(s.employer.epf_employer)}</TableCell>
                          <TableCell className="text-right font-mono text-sm hidden md:table-cell">{fmtRM(s.deductions.pcb)}</TableCell>
                          <TableCell className="text-right font-mono text-sm font-bold">{fmtRM(s.net_pay)}</TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" data-testid={`view-payslip-${s.emp_no}`} onClick={() => setSlip(s)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <PayslipModal payslip={slip} open={!!slip} onClose={() => setSlip(null)} company={tenant?.company_name} />
    </div>
  );
}
